/**
 * Stock position for the field — the Business Central figures a salesperson
 * acts on, trimmed to four columns: what is in total, the sale price per Pet
 * (box) and per piece, and whether the item needs attention.
 *
 * Reached from the "Stock" tab and the dashboard's "Van stock remaining" tile.
 * Read-only.
 */

import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { Page } from '../../components/Shell';
import {
  Banner, Card, Chip, ChipRow, Empty, ErrorState, Money, Segment, Skeleton, TextInput,
} from '../../components/ui';
import { useQuery, useDebounced, useRefreshOnFocus } from '../../lib/useApi';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { mediaUrl } from '../../lib/api';
import { piecesAndPacks } from '../../lib/format';
import type { PackInfo } from '../../lib/format';

/** `/api/field/van` — the driver's whole van, aggregated across active sheets. */
interface VanItem {
  productId: string; skuCode: string; productName: string;
  category: string | null; packUom: string | null; packQty: number | null;
  startingPieces: number; startingPets: number; startingLoose: number;
  deliveredPieces: number; deliveredPets: number; deliveredLoose: number;
  remainingPieces: number; remainingPets: number; remainingLoose: number;
}
interface VanResponse {
  hasVan: boolean;
  allConfirmed: boolean;
  pendingConfirm: number;
  items: VanItem[];
}

/** "X Pets & Y Pieces" — the one unit the Stock-in-Van tables speak. */
function petsPieces(pets: number, pieces: number): string {
  return `${pets} Pets & ${pieces} Pieces`;
}

// Four-column Stock-in-Van table styling (Phase 6), shared across rows.
const VAN_TABLE: CSSProperties = {
  width: '100%', borderCollapse: 'collapse', minWidth: 580, fontSize: 13,
};
const VAN_TH: CSSProperties = {
  textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--ink-2, #555)', borderBottom: '1px solid var(--line-2)', whiteSpace: 'nowrap',
};
const VAN_TH_LEFT: CSSProperties = { ...VAN_TH, textAlign: 'left' };
const VAN_TD: CSSProperties = {
  textAlign: 'right', padding: '9px 10px', borderBottom: '1px solid var(--line-2)',
  whiteSpace: 'nowrap', fontWeight: 600,
};
const VAN_TD_LEFT: CSSProperties = {
  ...VAN_TD, textAlign: 'left', overflowWrap: 'anywhere', whiteSpace: 'normal',
};

interface BalanceRow {
  productId: string;
  skuCode: string;
  productName: string;
  uom: string;
  baseUom: string | null;
  packUom: string | null;
  packQty: number | null;
  imageUrl: string | null;
  bcTotal: number | null;
  salePricePaise: number;
  category: string | null;
  status: 'out_of_stock' | 'below_reorder' | 'low_cover' | 'ok' | 'unknown';
}

interface BalancesResponse {
  items: BalanceRow[];
  source: { stale: boolean; staleMinutes: number | null; note: string };
}

const STATUS_CHIP: Record<BalanceRow['status'], { tone: 'danger' | 'warn' | 'info' | 'ok' | 'neutral'; label: string }> = {
  out_of_stock: { tone: 'danger', label: 'Out' },
  below_reorder: { tone: 'warn', label: 'Reorder' },
  low_cover: { tone: 'info', label: 'Low cover' },
  ok: { tone: 'ok', label: 'OK' },
  unknown: { tone: 'neutral', label: 'Not reported' },
};

// The same four core categories as the dashboard's "Billed outlets reach", so
// the two screens speak the same language. Matched on the product's category
// name by keyword, since BC's category labels vary a little.
const CATS = [
  // `emergy` too — Business Central has the category misspelled "EMERGY DRINK",
  // so matching only /energy/ left the Energy pill empty and its filter blank.
  { key: 'energy', label: 'Energy Drink', re: /energy|emergy/i },
  { key: 'cold', label: 'Cold Drink', re: /cola|cold|soft/i },
  { key: 'water', label: 'Campa Water', re: /water/i },
  { key: 'juice', label: 'Juice', re: /juice/i },
] as const;

function packOf(r: BalanceRow): PackInfo {
  return { baseUom: r.baseUom, packUom: r.packUom, packQty: r.packQty };
}

export default function Stock() {
  const t = useT();
  const [cat, setCat] = useState<string>('all');
  const [search, setSearch] = useState('');
  const query = useDebounced(search);

  // Godown (detailed BC inventory) vs Van (itemized real van position).
  const [view, setView] = useState<'godown' | 'van'>('godown');
  const { user } = useAuth();
  // A combined driver-cum-salesperson runs a van too, so they see the real van
  // stock here — not the "shown on the driver's app" placeholder meant for a
  // pure salesperson who has no van.
  const isDriver = user?.role === 'driver' || user?.role === 'field_agent';

  const { data, loading, error, refetch } = useQuery<BalancesResponse>('/api/stock/balances');
  useRefreshOnFocus(refetch);

  /**
   * Van view — Phase 5. The REAL van, aggregated across every active load
   * sheet the driver is carrying, itemised, grouped by category, each product
   * showing its available Pets & Pieces. Fetched only while the Van toggle is
   * active and only for a driver (the endpoint returns an empty van for a
   * salesperson).
   *
   * /api/field/van is the ONE source — it sums starting and remaining across
   * all the driver's sheets, which is what the van physically holds. The old
   * single-sheet /van-prefill missed deliveries made against another sheet;
   * this does not.
   */
  const van = useQuery<VanResponse>(
    view === 'van' && isDriver ? '/api/field/van' : null,
  );

  /**
   * The van's items grouped by the four core categories. Each product's
   * remaining is already split server-side into whole Pets and loose Pieces
   * against its own pack size (a 1×12 water and a 1×30 juice differ). Per
   * category we carry the aggregate balance (pets + loose pieces) and the
   * loaded total, for the green balance / red loaded display. Rows with
   * nothing left are dropped from the item list — but a fully-sold item still
   * counts toward the category's loaded total, so the "out of" figure does
   * not shrink as the van empties.
   */
  const vanGroups = useMemo(() => {
    type Cell = { pets: number; pieces: number };
    type Item = { name: string; sku: string; loaded: Cell; delivered: Cell; balance: Cell };
    const groups = CATS.map((c) => ({
      key: c.key,
      label: c.label,
      re: c.re,
      loaded: { pets: 0, pieces: 0 } as Cell,
      delivered: { pets: 0, pieces: 0 } as Cell,
      balance: { pets: 0, pieces: 0 } as Cell,
      items: [] as Item[],
    }));
    for (const item of van.data?.items ?? []) {
      const g = groups.find((x) =>
        (item.category && x.re.test(item.category)) || x.re.test(item.productName));
      if (!g) continue;
      g.loaded.pets += item.startingPets;
      g.loaded.pieces += item.startingLoose;
      g.delivered.pets += item.deliveredPets;
      g.delivered.pieces += item.deliveredLoose;
      g.balance.pets += item.remainingPets;
      g.balance.pieces += item.remainingLoose;
      // Every product that was loaded is listed, including sold-out ones, so
      // the driver sees the full round and not only what is left — the
      // "Order Delivered" column is the point of the row.
      g.items.push({
        name: item.productName,
        sku: item.skuCode,
        loaded: { pets: item.startingPets, pieces: item.startingLoose },
        delivered: { pets: item.deliveredPets, pieces: item.deliveredLoose },
        balance: { pets: item.remainingPets, pieces: item.remainingLoose },
      });
    }
    return groups.filter((g) => g.items.length > 0);
  }, [van.data]);

  const rows = useMemo(() => {
    let items = data?.items ?? [];
    if (cat !== 'all') {
      const def = CATS.find((c) => c.key === cat);
      if (def) items = items.filter((i) => i.category && def.re.test(i.category));
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      items = items.filter((i) =>
        i.productName.toLowerCase().includes(q) || i.skuCode.toLowerCase().includes(q));
    }
    // Worst first, so an item that needs attention sits at the top.
    const rank: Record<BalanceRow['status'], number> = {
      out_of_stock: 0, below_reorder: 1, low_cover: 2, unknown: 3, ok: 4,
    };
    return [...items].sort((a, b) =>
      rank[a.status] - rank[b.status] || a.productName.localeCompare(b.productName));
  }, [data, cat, query]);

  // Counts per category pill, so the salesperson sees how many SKUs sit in each.
  const catCount = (def: typeof CATS[number]) =>
    (data?.items ?? []).filter((i) => i.category && def.re.test(i.category)).length;

  return (
    <Page title="Stock">
      <div className="stack">
        <Segment
          value={view}
          onChange={setView}
          options={[
            { value: 'godown', label: 'Stock in Godown' },
            { value: 'van', label: 'Stock in Van' },
          ]}
        />

        {view === 'van' ? (
          /* Van view — the real van, itemised per product, Pets & Pieces. */
          <>
            {!isDriver && (
              <Banner tone="info">
                <span className="t-sm">Van stock is shown on the driver&apos;s app.</span>
              </Banner>
            )}
            {isDriver && van.loading && !van.data && <Skeleton rows={6} />}
            {isDriver && van.data && !van.data.hasVan && (
              <Empty title="No van loaded today" hint="Stock appears here once your van is loaded and confirmed." />
            )}
            {isDriver && van.data?.hasVan && (
              <>
                <div className="row-between" style={{ alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Stock in Van</h3>
                  {van.data.allConfirmed
                    ? <Chip tone="ok">Confirmed</Chip>
                    : <Chip tone="warn">Pending confirm</Chip>}
                </div>
                {vanGroups.length === 0 ? (
                  <Empty title="The van is empty" hint="Everything loaded has been delivered or returned." />
                ) : (
                  /*
                    Phase 6 — one four-column table: Category/Product · Loaded in
                    Van · Order Delivered · Balance in Van, each cell in Pets &
                    Pieces. Category header rows are bold with a tint; the
                    products under each are indented. Horizontal scroll on a
                    phone so four verbose columns never overflow.
                  */
                  <Card flush>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="van-table" style={VAN_TABLE}>
                        <thead>
                          <tr>
                            <th style={VAN_TH_LEFT}>Category / Product</th>
                            <th style={VAN_TH}>Loaded in Van</th>
                            <th style={VAN_TH}>Order Delivered</th>
                            <th style={VAN_TH}>Balance in Van</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vanGroups.map((g) => (
                            <Fragment key={g.key}>
                              <tr style={{ background: 'var(--line-2)' }}>
                                <td style={{ ...VAN_TD_LEFT, textTransform: 'uppercase', letterSpacing: '.02em' }}>
                                  {g.label}
                                </td>
                                <td style={{ ...VAN_TD, color: 'var(--danger)', fontWeight: 700 }}>
                                  {petsPieces(g.loaded.pets, g.loaded.pieces)}
                                </td>
                                <td style={{ ...VAN_TD, color: 'var(--ink-2, #555)', fontWeight: 700 }}>
                                  {petsPieces(g.delivered.pets, g.delivered.pieces)}
                                </td>
                                <td style={{ ...VAN_TD, color: 'var(--ok)', fontWeight: 800 }}>
                                  {petsPieces(g.balance.pets, g.balance.pieces)}
                                </td>
                              </tr>
                              {g.items.map((it) => (
                                <tr key={it.sku}>
                                  <td style={{ ...VAN_TD_LEFT, paddingLeft: 22, fontWeight: 500 }}>
                                    {it.name}
                                    <span className="t-xs t-muted" style={{ display: 'block' }}>{it.sku}</span>
                                  </td>
                                  <td style={{ ...VAN_TD, color: 'var(--danger)' }}>
                                    {petsPieces(it.loaded.pets, it.loaded.pieces)}
                                  </td>
                                  <td style={{ ...VAN_TD, color: 'var(--ink-2, #555)' }}>
                                    {petsPieces(it.delivered.pets, it.delivered.pieces)}
                                  </td>
                                  <td style={{ ...VAN_TD, color: 'var(--ok)', fontWeight: 700 }}>
                                    {petsPieces(it.balance.pets, it.balance.pieces)}
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </>
            )}
          </>
        ) : (
          <>
        {loading && !data && <Skeleton rows={6} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}

        {data && (
          <>
            <TextInput
              placeholder={t('app.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('app.search')}
            />

            <ChipRow
              value={cat}
              onChange={setCat}
              options={[
                { value: 'all', label: 'Total', count: data.items.length },
                ...CATS.map((c) => ({ value: c.key, label: c.label, count: catCount(c) })),
              ]}
            />

            {/* Mobile-first card list: every field (quantity, price, status)
                sits beneath the product name, so nothing is pushed off a
                narrow screen the way a table's columns were. */}
            {rows.length === 0 ? (
              <Empty title="Nothing matches" />
            ) : (
              <Card flush>
                <div className="list">
                  {rows.map((r) => {
                    const per = r.packQty && r.packQty > 1 ? r.packQty : null;
                    return (
                      <div
                        key={r.productId}
                        className="list__item list__item--static"
                        style={{ alignItems: 'flex-start', gap: 10 }}
                      >
                        {r.imageUrl && (
                          <img
                            src={mediaUrl(r.imageUrl)}
                            alt=""
                            className="thumb--sm"
                            loading="lazy"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        )}
                        <div className="grow" style={{ minWidth: 0 }}>
                          <div className="t-semi t-sm" style={{ overflowWrap: 'anywhere' }}>{r.productName}</div>
                          <div className="t-xs t-muted">{r.skuCode}</div>

                          {/* Quantity · Price · Status, wrapping as needed */}
                          <div
                            style={{
                              display: 'flex', flexWrap: 'wrap', gap: '6px 16px',
                              marginTop: 8, alignItems: 'flex-end',
                            }}
                          >
                            <div>
                              <div className="t-xs t-faint">Total (BC)</div>
                              <div className="t-sm t-semi">
                                {r.bcTotal === null ? '—' : piecesAndPacks(r.bcTotal, packOf(r))}
                              </div>
                            </div>

                            <div>
                              <div className="t-xs t-faint">Sale price / Pet</div>
                              <div className="t-sm t-semi">
                                {r.salePricePaise <= 0 ? (
                                  <span className="t-muted">—</span>
                                ) : (
                                  <>
                                    <Money paise={per ? r.salePricePaise * per : r.salePricePaise} className="t-semi" />
                                    <span className="t-xs t-muted">
                                      {' · '}
                                      {per ? <><Money paise={r.salePricePaise} /> / pc</> : 'per piece'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <div style={{ marginLeft: 'auto' }}>
                              <Chip tone={STATUS_CHIP[r.status].tone}>{STATUS_CHIP[r.status].label}</Chip>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </>
        )}
          </>
        )}
      </div>
    </Page>
  );
}
